import { Formik } from 'formik';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import * as Yup from 'yup';
import {
  ActionButton,
  Buttons,
  CancelButton,
  Drawer,
  Field,
  Form,
  FormMessage,
  Radio,
  Stack,
  Tooltip,
} from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { useDirtyCheck, useDocumentTitle, useForm } from '~/hooks';
import { ErrorPage } from '~/routes/public/pages';
import { colors, weights } from '~/styles';
import { calculateMarkupRatio, emptyStringToNull, getInitialMarkupRatioValue, mergeValues } from '~/utils';
import AggregatedBudgetMembersTable from './AggregatedBudgetMembersTable';
import AggregatedBudgetRolesTable from './AggregatedBudgetRolesTable';
import { BudgetDrawerContext } from './BudgetDrawerContext';
import BudgetExpensesTable from './BudgetExpensesTable';
import BudgetForm from './BudgetForm';
import BudgetHoursAlertSetting from './BudgetHoursAlertSetting';
import BudgetMembersTable from './BudgetMembersTable';
import BudgetOtherItemsTable from './BudgetOtherItemsTable';
import BudgetProductItemsTable from './BudgetProductItemsTable';
import BudgetRevenueAlertSetting from './BudgetRevenueAlertSetting';
import BudgetRolesTable from './BudgetRolesTable';
import BudgetServiceItemsTable from './BudgetServiceItemsTable';
import CapAssignedHoursCheckbox from './CapAssignedHoursCheckbox';
import CapExpensesCostCheckbox from './CapExpensesCostCheckbox';
import CapExpensesRevenueCheckbox from './CapExpensesRevenueCheckbox';
import CapHoursCheckbox from './CapHoursCheckbox';
import CapRevenueCheckbox from './CapRevenueCheckbox';
import FinancialSummary from './FinancialSummary';

const ErrorMessage = styled(FormMessage.Error)`
  margin-bottom: 1.5rem;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-top: 1.625rem;
  margin-bottom: 1.625rem;
`;

const ProjectSettings = styled.div`
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;

  > label {
    display: inline-flex;

    &:not(:first-child) {
      margin-left: 1.5rem;
    }
  }

  > div:not(:first-child) {
    margin-left: 0.5rem;
  }
`;

const Separator = styled.hr`
  margin: ${({ margin }) => margin ?? '0rem 0 1.25rem'};
  border-color: ${colors.grey10};
`;

const TableTitle = styled.span`
  flex: 0;
  color: ${colors.grey75};
  padding-left: 0rem;
  font-size: 0.875rem;
  font-weight: ${weights.bold};
  padding-top: 1rem;
`;

function EditBudgetDrawer({ projectId, onSaved, onClose }) {
  useDocumentTitle('Edit Budget');

  const api = useApi();
  const { workspace } = useWorkspace();
  const [{ status, message, isSubmitting, saved }, form] = useForm();
  const [{ isReady, project }, setData] = useState(() => ({ project: null, isReady: false }));
  const actions = useMemo(() => {
    return {
      ready: ({ project }) => setData((state) => ({ ...state, project, isReady: true })),
    };
  }, []);

  const location = useLocation();

  const [forms, setForms] = useState([]);

  const drawerRef = useRef();
  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty || forms.length > 0);

  const fetchData = useCallback(async () => {
    try {
      const { data: project } = await api.www.workspaces(workspace.id).projectSettings(projectId).budget();
      actions.ready({ project });
      return project;
    } catch (error) {
      actions.ready({ project: null });
    }
  }, [actions, workspace.id, projectId, api]);

  useEffect(() => {
    if (project) return;
    fetchData();
  }, [fetchData, project]);

  function handleClose() {
    if (onClose) {
      onClose();
    }
  }

  if (!isReady) return null;
  if (!project) return <ErrorPage.NotFound publicSite={false} />;

  if (!project.permissions.edit) {
    return <Redirect to={location.pathname.replace('/edit', '')} />;
  }

  const initialValues = mergeValues(
    {
      // Project (read-only)
      client: project.client,
      isBillable: project.isBillable,
      billingTypeId: project.billingTypeId,
      useRoles: project.useRoles,
      members: project.members,
      roles: project.roles,
      currency: project.currency,
      useBudget: project.useBudget,
      revenueRecognitionMethod: project.revenueRecognitionMethod,

      // Budget
      budgetMode: '',
      estimatedBillableHours: '',
      estimatedNonBillableHours: '',
      estimatedServiceRevenue: '',
      budgetExpensesBillableAmount: '',
      budgetExpensesNonBillableAmount: '',
      budgetExpenses: [],
      budgetProductItemsFee: '',
      budgetProductItems: [],
      budgetServiceItemsFee: '',
      budgetServiceItems: [],
      budgetOtherItemsFee: '',
      budgetOtherItems: [],
      capRevenue: false,
      capHours: false,
      capAssignedHours: false,
      capExpenses: false,
      capExpensesCost: false,
      capExpensesRevenue: false,
      budgetServicesGrossMargin: '',
      budgetEnableMarkup: false,
      budgetMarkupAmount: '',
      budgetMarkupRatio: '',
      budgetMarkupMethod: 'percentage',

      // Budget Alerts
      budgetHoursAlert: false,
      budgetHoursAlertThreshold: 0.8,
      budgetRevenueAlert: false,
      budgetRevenueAlertThreshold: 0.8,
    },
    {
      ...project,
      budgetServicesGrossMargin: _.isNumber(project.budgetServicesGrossMargin)
        ? _.round(project.budgetServicesGrossMargin * 100, 2)
        : '',
      budgetMarkupRatio: getInitialMarkupRatioValue({
        markupRatio: project.budgetMarkupRatio,
        defaultMarkupPercentage: workspace.defaultMarkupPercentage,
      }),
      budgetEnableMarkup: !!project.budgetMarkupMethod,
    },
  );

  const schema = Yup.object().shape({
    estimatedBillableHours: Yup.number().label('Billable Hours Budget').min(0).max(999999.99).nullable(),
    estimatedNonBillableHours: Yup.number().label('Non-Billable Hours Budget').min(0).max(999999.99).nullable(),
    estimatedServiceRevenue: Yup.number()
      .label('Services Revenue Budget')
      .max(99999999999)
      .nullable()
      .when(['revenueRecognitionMethod', 'billingTypeId'], (revenueRecognitionMethod, billingTypeId, schema) =>
        ['automated_hours', 'automated_tm_basis'].includes(revenueRecognitionMethod) &&
        ['fixed', 'fixed_recurring'].includes(billingTypeId)
          ? schema
              .min(1)
              .required('Services Revenue Budget is a required field for the selected Revenue Recognition Method.')
          : schema.min(0),
      ),
    budgetExpensesBillableAmount: Yup.number().label('Billable Expenses Budget').min(0).max(99999999999).nullable(),
    budgetExpensesNonBillableAmount: Yup.number()
      .label('Non-Billable Expenses Budget')
      .min(0)
      .max(99999999999)
      .nullable(),
    budgetProductItemsFee: Yup.number().label('Products Budget').min(-99999999999).max(99999999999).nullable(),
    budgetServiceItemsFee: Yup.number()
      .label('Additional Services Budget')
      .min(-99999999999)
      .max(99999999999)
      .nullable(),
    budgetOtherItemsFee: Yup.number().label('Other Items Budget').min(-99999999999).max(99999999999).nullable(),
    budgetMarkupMethod: Yup.string().label('Type'),
    budgetMarkupAmount: Yup.number().label('Markup Amount').min(0).max(99999999999).nullable(),
    budgetMarkupRatio: Yup.number().label('Markup Percentage').min(0).max(100).nullable(),
  });

  return (
    <BudgetDrawerContext.Provider value={{ forms, setForms }}>
      <Drawer
        isOpen
        title="Edit Budget"
        byline={project.name}
        ref={drawerRef}
        onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
        onClose={handleClose}
        width="70rem">
        {(closeDrawer) => {
          const handleCloseClick = () => dirtyCheck(() => closeDrawer());

          async function handleSubmit(values) {
            try {
              form.submit(formRef.current?.status?.action);

              const body = emptyStringToNull({
                ..._.omit(values, [
                  'members',
                  'budgetExpenses',
                  'client',
                  'isBillable',
                  'billingTypeId',
                  'useRoles',
                  'currency',
                  'useBudget',
                  'revenueRecognitionMethod',
                  'capExpenses',
                  'budgetEnableMarkup',
                ]),
                members: values.members.map((pm) =>
                  _.pick(pm, ['id', 'estimatedBillableHours', 'estimatedNonBillableHours']),
                ),
                roles: values.roles.map((pr) =>
                  _.pick(pr, 'id', 'estimatedBillableHours', 'estimatedNonBillableHours'),
                ),
                budgetExpenses: values.budgetExpenses.map((be) =>
                  _.pick(
                    be,
                    'id',
                    'nonBillableAmount',
                    'billableAmount',
                    'expenseCategoryId',
                    'notes',
                    'markupMethod',
                    'markupAmount',
                    'markupRatio',
                  ),
                ),
                budgetOtherItems: values.budgetOtherItems.map((boi) =>
                  _.pick(boi, 'id', 'invoiceItemId', 'description', 'fee'),
                ),
                budgetProductItems: values.budgetProductItems.map((bpi) =>
                  _.pick(bpi, 'id', 'invoiceItemId', 'description', 'fee'),
                ),
                budgetServiceItems: values.budgetServiceItems.map((bsi) =>
                  _.pick(bsi, 'id', 'invoiceItemId', 'description', 'fee'),
                ),
                budgetServicesGrossMargin: _.isNumber(values.budgetServicesGrossMargin)
                  ? values.budgetServicesGrossMargin / 100
                  : null,
                budgetMarkupMethod: values.budgetEnableMarkup ? values.budgetMarkupMethod : null,
                budgetMarkupAmount: values.budgetEnableMarkup ? values.budgetMarkupAmount : null,
                budgetMarkupRatio: calculateMarkupRatio({
                  markupRatio: values.budgetMarkupRatio,
                  enableMarkup: values.budgetEnableMarkup,
                }),
                // A non-billable project has no revenue-side cap checkbox; the disabled checkbox mirrors the
                // cost cap, so the stored value has to as well.
                capExpensesRevenue: values.isBillable ? values.capExpensesRevenue : values.capExpensesCost,
              });

              await api.www.workspaces(workspace.id).projectSettings(project?.id).updateBudget(body);
              const { data } = await api.www.workspaces(workspace.id).projectSettings(project.id).budget();
              await onSaved(data);

              form.save(formRef.current.status.action);

              if (formRef.current.status.action === 'close') {
                closeDrawer();
              } else {
                actions.ready({ project: data });
              }
            } catch ({ message }) {
              drawerRef.current.scrollTo({ top: 0 });
              form.error({ message });
            }
          }

          return (
            <>
              <Formik
                innerRef={formRef}
                enableReinitialize
                initialValues={initialValues}
                onSubmit={handleSubmit}
                validateOnBlur={false}
                validateOnChange={false}
                validationSchema={schema}>
                {(formik) => {
                  const { submitForm, setStatus } = formik;

                  const budget = formik.values;
                  const currency = budget.currency;
                  const summaryBudgetDisabled =
                    ['fixed', 'fixed_recurring'].includes(budget.billingTypeId) &&
                    budget.revenueRecognitionMethod === 'automated_tm_basis';

                  const submit = async (action) => {
                    // Submit all open inline forms
                    for await (const form of forms) {
                      await form.ref.current.submitForm();
                    }

                    // If at least one form is invalid, don't submit the drawer
                    for await (const form of forms) {
                      if (form.ref.current && !form.ref.current.isValid) return;
                    }

                    setStatus({ action });
                    submitForm();
                  };

                  return (
                    <Stack data-testid="edit_budget_drawer">
                      {status && <ErrorMessage>{message || 'An error has occurred.'}</ErrorMessage>}

                      <Content>
                        <ProjectSettings>
                          <Field.RadioGroup name="budgetMode">
                            <Tooltip
                              message={
                                summaryBudgetDisabled
                                  ? 'A summary budget cannot be used with the selected Revenue Recognition Method.'
                                  : undefined
                              }>
                              <Radio value="" label="Summary budget" disabled={summaryBudgetDisabled} />
                            </Tooltip>
                            <Radio value="detailed" label="Detailed budget" />
                            <Radio value="aggregated" label="Detailed budget aggregated from tasks" />
                          </Field.RadioGroup>
                        </ProjectSettings>

                        {!budget.budgetMode ? (
                          <BudgetForm formik={formik} project={project} />
                        ) : (
                          <>
                            {['fixed', 'fixed_recurring'].some((b) => b === budget.billingTypeId) && (
                              <>
                                <TableTitle>Services Revenue</TableTitle>
                                <Form.Control>
                                  <Field.Currency
                                    name="estimatedServiceRevenue"
                                    placeholder="Amount"
                                    currency={currency}
                                  />
                                </Form.Control>
                              </>
                            )}

                            {budget.isBillable && budget.billingTypeId === 'tm' ? (
                              <>
                                <Separator margin="0" />
                                <TableTitle>Hours and Services Revenue</TableTitle>
                              </>
                            ) : (
                              <TableTitle>Hours</TableTitle>
                            )}

                            <Form.Control>
                              {
                                {
                                  detailed: budget.useRoles ? (
                                    <BudgetRolesTable
                                      project={budget}
                                      onChange={(value) => formik.setFieldValue('roles', value)}
                                    />
                                  ) : (
                                    <BudgetMembersTable
                                      project={budget}
                                      onChange={(value) => formik.setFieldValue('members', value)}
                                    />
                                  ),
                                  aggregated: budget.useRoles ? (
                                    <AggregatedBudgetRolesTable project={budget} />
                                  ) : (
                                    <AggregatedBudgetMembersTable project={budget} />
                                  ),
                                }[budget.budgetMode]
                              }
                            </Form.Control>

                            <Form.Control>
                              <CapRevenueCheckbox project={budget} />
                            </Form.Control>

                            <Form.Control>
                              <BudgetRevenueAlertSetting project={budget} />
                            </Form.Control>

                            <Form.Control>
                              <CapHoursCheckbox />
                            </Form.Control>

                            <Form.Control>
                              <CapAssignedHoursCheckbox useRoles={project.useRoles} />
                            </Form.Control>

                            <Form.Control>
                              <BudgetHoursAlertSetting project={budget} />
                            </Form.Control>

                            <TableTitle>Expenses</TableTitle>
                            <Form.Control>
                              <BudgetExpensesTable
                                project={budget}
                                onChange={(value) => formik.setFieldValue('budgetExpenses', value)}
                              />
                            </Form.Control>

                            <Form.Control>
                              <CapExpensesCostCheckbox />
                            </Form.Control>

                            <Form.Control>
                              <Form.Control>
                                {budget.isBillable ? (
                                  <CapExpensesRevenueCheckbox />
                                ) : (
                                  <CapExpensesRevenueCheckbox disabled={true} checked={budget.capExpensesCost} />
                                )}
                              </Form.Control>
                            </Form.Control>

                            {budget.isBillable && (
                              <>
                                <TableTitle>Products</TableTitle>
                                <Form.Control>
                                  <BudgetProductItemsTable
                                    project={budget}
                                    onChange={(value) => formik.setFieldValue('budgetProductItems', value)}
                                  />
                                </Form.Control>

                                <TableTitle>Additional Services</TableTitle>
                                <Form.Control>
                                  <BudgetServiceItemsTable
                                    project={budget}
                                    onChange={(value) => formik.setFieldValue('budgetServiceItems', value)}
                                  />
                                </Form.Control>

                                <TableTitle>Other Items</TableTitle>
                                <Form.Control>
                                  <BudgetOtherItemsTable
                                    project={budget}
                                    onChange={(value) => formik.setFieldValue('budgetOtherItems', value)}
                                  />
                                </Form.Control>
                              </>
                            )}
                          </>
                        )}

                        {budget.isBillable && <FinancialSummary project={project} editedProject={budget} />}
                      </Content>

                      <Drawer.Actions>
                        <Buttons align="right">
                          <CancelButton onClick={handleCloseClick}>Close</CancelButton>

                          <ActionButton
                            isOutline
                            isLoading={isSubmitting === 'stay'}
                            ok={saved === 'stay'}
                            onClick={() => submit('stay')}>
                            Save
                          </ActionButton>

                          <ActionButton
                            isLoading={isSubmitting === 'close'}
                            ok={saved === 'close'}
                            onClick={() => submit('close')}>
                            Save &amp; Close
                          </ActionButton>
                        </Buttons>
                      </Drawer.Actions>
                    </Stack>
                  );
                }}
              </Formik>
            </>
          );
        }}
      </Drawer>
    </BudgetDrawerContext.Provider>
  );
}

export default EditBudgetDrawer;
